import { Component, inject, input, linkedSignal, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_CARD, KENDO_GRIDLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractNewComponent } from '@unity/abstract/abstract-new.component';
import { AgencyMiniDto, CreatedResponseDto, RoleCreateDto, RolesClient } from '@unity/clients';
import { DropdownAgencyControlComponent } from '@unity/forms/dropdown-agency-control/dropdown-agency-control.component';
import { DropdownControlComponent } from '@unity/forms/dropdown-control/dropdown-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { CardNewSharedComponent } from '@unity/shared/composed/card-new-shared/card-new-shared.component';
import { nonZeroFormControl, stringFormControl } from '@unity/utils/form-controls';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';

class RoleCreationSchema implements FormGroupControlSchema<RoleCreateDto> {
  public name = stringFormControl();
  public description = stringFormControl();
  public agency_id = nonZeroFormControl();
  public parent_role_id = nonZeroFormControl();

  public constructor(agencyId: number) {
    this.agency_id = nonZeroFormControl({ value: agencyId });
  }
}

@Component({
  selector: 'unity-new-role-page',
  imports: [
    KENDO_GRIDLAYOUT,
    KENDO_BUTTON,
    KENDO_LOADER,
    KENDO_CARD,
    ReactiveFormsModule,
    DropdownAgencyControlComponent,
    DropdownControlComponent,
    TextboxControlComponent,
    PageContainerComponent,
    CardNewSharedComponent,
  ],
  templateUrl: './new-role-page.component.html',
  styleUrl: './new-role-page.component.scss',
})
export class NewRolePageComponent extends AbstractNewComponent<RoleCreateDto, RoleCreationSchema, CreatedResponseDto> {
  private readonly rolesClient = inject(RolesClient);

  public readonly $agencyId = input.required({ alias: 'agencyId', transform: (value: string) => parseInt(value) });

  protected override readonly $contentHeader = signal('Create New Role');
  protected override createResourceFn$ = (form: FormGroup<RoleCreationSchema>) => this.rolesClient.createOne(form.getRawValue());
  protected override redirectUrlFn = (response: CreatedResponseDto): UnityRouteUrlTrees => [
    '/business-partner',
    this.$agencyId(),
    'role',
    response.id,
  ];
  protected override newControlsFn(): RoleCreationSchema {
    const agencyId = this.$agencyId();
    return new RoleCreationSchema(agencyId ?? 0);
  }

  protected readonly $selectedAgencyId = linkedSignal(() => this.$agencyId());

  protected validRolesResource = rxResource({
    params: () => {
      const agencyId = this.$selectedAgencyId();
      return agencyId ? { agencyId } : undefined;
    },
    stream: ({ params: { agencyId } }) => this.rolesClient.getAllAsDetailed(agencyId),
  });

  protected onAgencySelection(agency: AgencyMiniDto) {
    this.$selectedAgencyId.set(agency.id);
    this.$formGroup().controls.parent_role_id.reset();
  }

  protected $inputRoles = this.validRolesResource.value;
}
